/**
 * Syncs pending chart update approvals into the bell notification system.
 *
 * Polls /api/chart-updates/pending every 60s and adds one notification per
 * pending update that hasn't been surfaced yet.  When updates are approved or
 * rejected, the notification is automatically cleaned up on the next poll cycle
 * because useApiGetAuth re-fetches fresh data.
 */
import { useEffect, useRef, useContext } from 'react';
import { NotificationContext } from '../contexts/NotificationContext';
import { useApiGetAuth } from './useApiQuery';
import { useAuthReady } from './useAuthReady';
import { getOwnerUserId } from '../utils/identity';
import { isClientUser } from '../app/router/helpers';
import { EnteredBy, actorLabel } from '../utils/chartUpdateProvenance';

interface PendingChartUpdate {
  id: string;
  patient_id: string;
  field_path: string;
  proposed_value: string;
  created_at: string;
  entered_by?: EnteredBy | null;
}

export function useChartUpdateNotifications() {
  const { ready, user } = useAuthReady();
  const ctx = useContext(NotificationContext);
  const addNotification = ctx?.addNotification;

  const ownerUserId = ready ? getOwnerUserId(user) : null;
  // The /api/chart-updates/pending endpoint requires staff role — skip for
  // client portal users to avoid 403 errors.
  const isStaff = ready && !!user && !isClientUser(user);
  const { data: pending = [] } = useApiGetAuth<PendingChartUpdate[]>(
    ownerUserId && isStaff ? ['chart-updates', 'pending', 'bell'] : null,
    '/api/chart-updates/pending',
    { refetchInterval: 60000 },
  );

  // Track which update IDs we've already surfaced so we don't re-add on re-poll.
  const surfacedIdsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!addNotification || !Array.isArray(pending)) return;

    for (const update of pending) {
      if (surfacedIdsRef.current.has(update.id)) continue;
      surfacedIdsRef.current.add(update.id);

      const fieldLabel = update.field_path
        .replace(/\[\d+]/, '')
        .split('.')
        .map(p => p.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
        .join(' › ');

      addNotification({
        type: 'chart_update_pending',
        title: 'Chart Update Needs Approval',
        message: `${actorLabel(update.entered_by)} submitted a change to ${fieldLabel}`,
        link: `/patients/${update.patient_id}`,
      });
    }
  }, [pending, addNotification]);
}
